import React, { forwardRef } from 'react';
import { OutlinedInput, InputAdornment } from '@mui/material';

const inputStyle = {
  height: '44px',
  borderColor: '#E5E7EB',
  borderRadius: '6px',
};

// Using forwardRef to handle ref forwarding safely
const NumberInput = forwardRef(
  (
    {
      value,
      onChange,
      placeholder = 'Enter Value',
      unit,
      onBlur,
      onKeyDown,
      inputProps,
      ...rest
    },
    ref
  ) => {
    const handleChange = (event) => {
      const str = event.target.value ?? '';
      const sanitized = str.replace(/[^0-9.]/g, '');

      if (sanitized === '' || sanitized === '.') {
        onChange?.('');
        return;
      }

      const [integerPart, ...decimalParts] = sanitized.split('.');
      const normalized = decimalParts.length
        ? `${integerPart}.${decimalParts.join('')}`
        : integerPart;

      if (normalized === '' || normalized === '.') {
        onChange?.('');
        return;
      }

      if (sanitized.endsWith('.') && decimalParts.length <= 1) {
        onChange?.(normalized);
        return;
      }

      const newValue = Number(normalized);
      if (Number.isNaN(newValue)) {
        return;
      }

      onChange?.(newValue);
    };

    const handleBlur = (event) => {
      const raw = event.target.value ?? '';
      if (raw === '' || raw === '.') {
        onChange?.('');
      } else {
        const parsed = Number(raw);
        if (!Number.isNaN(parsed)) {
          onChange?.(parsed);
        }
      }

      onBlur?.(event);
    };

    const handleKeyDown = (event) => {
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault();
        const raw = event.target.value ?? '';
        const numericValue = Number(raw);
        const minConstraint =
          inputProps && inputProps.min !== undefined
            ? Number(inputProps.min)
            : undefined;
        const step =
          inputProps && inputProps.step !== undefined
            ? Number(inputProps.step)
            : 1;

        let nextValue;
        if (Number.isNaN(numericValue)) {
          nextValue = event.key === 'ArrowUp' ? (step || 1) : 0;
        } else if (event.key === 'ArrowUp') {
          nextValue = numericValue + step;
        } else {
          nextValue = numericValue - step;
        }

        if (!Number.isNaN(minConstraint)) {
          nextValue = Math.max(nextValue, minConstraint);
        }

        onChange?.(nextValue);
        onKeyDown?.(event);
        return;
      }

      onKeyDown?.(event);
    };

    return (
      <OutlinedInput
        placeholder={placeholder}
        value={value !== undefined && value !== null ? value : ''}
        type='number'
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        endAdornment={
          unit && <InputAdornment position='end'>{unit}</InputAdornment>
        }
        sx={inputStyle}
        ref={ref} // Forward ref to the <input> element
        inputProps={inputProps}
        {...rest}
      />
    );
  }
);

export default NumberInput;
